// =============================================================================
// components/ui/Screen.tsx — the page shell every screen sits inside.
//
// WHY THIS EXISTS
// Tab screens render their own header (the Stack header is hidden) but did NOT
// apply a top safe-area inset, so on a notch / Dynamic Island device the header
// collided with the status bar; on older phones there was a dead band. Detail
// screens each re-implemented their own SafeAreaView + ScrollView + bottom
// padding with ad-hoc numbers. Screen centralises all of that:
//
//   - top safe-area inset (consistent across notch / Dynamic Island / iPad)
//   - lavender brand background + optional BackgroundBlobs
//   - optional scroll container with a bottom inset that clears the floating
//     tab bar (so the last row is never hidden behind it)
//   - a max content width on tablet / desktop (centres the column)
//
// USAGE
//   Scrolling screen:        <Screen><Section>…</Section></Screen>
//   FlatList-owning screen:  <Screen scroll={false}>…<FlatList/></Screen>
//     (pass `edges={['top']}` is the default; the list supplies its own bottom
//      contentContainerStyle padding — use `screenBottomInset` for the value.)
//   Already inside a Stack header (top inset handled by the native header):
//                            <Screen edges={[]}>…</Screen>
// =============================================================================
import React from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ViewStyle,
  StyleProp,
  RefreshControlProps,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, colorTokens, getLayout, layout as layoutTokens, ThemeMode } from '../../constants/theme';
import BackgroundBlobs from './BackgroundBlobs';

export type ScreenEdge = 'top' | 'bottom';

export interface ScreenProps {
  children: React.ReactNode;
  /** Wrap children in a ScrollView. Set false when the screen owns a FlatList. */
  scroll?: boolean;
  /** Safe-area edges to pad. Default ['top']. Use [] inside a native Stack header. */
  edges?: ScreenEdge[];
  /** Render the ambient lavender blobs behind content. Default true. */
  blobs?: boolean;
  /** Theme mode. 'light' (lavender, default) or 'dark' (voice/call). Additive. */
  theme?: ThemeMode;
  /** Pad the bottom to clear the floating tab bar (scroll mode only). Default true. */
  bottomNavInset?: boolean;
  /** Style for the outer flex container. */
  style?: StyleProp<ViewStyle>;
  /** Style for the scroll content container (scroll mode only). */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Cap + centre the content column on tablet/desktop. Default true. */
  constrainWidth?: boolean;
  /** Forwarded to the inner ScrollView (scroll mode only). */
  refreshControl?: React.ReactElement<RefreshControlProps>;
}

// Max readable column width per device class. undefined = full width (phone).
function maxWidthFor(width: number): number | undefined {
  const l = getLayout(width);
  if (l === 'desktop') return 720;
  if (l === 'tablet') return 620;
  return undefined;
}

export default function Screen({
  children,
  scroll = false,
  edges = ['top'],
  blobs = true,
  theme = 'light',
  bottomNavInset = true,
  style,
  contentContainerStyle,
  constrainWidth = true,
  refreshControl,
}: ScreenProps) {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const maxWidth = constrainWidth ? maxWidthFor(width) : undefined;

  const padTop = edges.includes('top') ? insets.top : 0;
  const padBottom = edges.includes('bottom') ? insets.bottom : 0;

  const constrain: ViewStyle | undefined = maxWidth
    ? { maxWidth, width: '100%', alignSelf: 'center' }
    : undefined;

  const bg = colorTokens[theme].bg;

  return (
    <View style={[styles.root, { backgroundColor: bg, paddingTop: padTop }, style]}>
      {blobs ? <BackgroundBlobs /> : null}
      {scroll ? (
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            bottomNavInset
              ? { paddingBottom: padBottom + layoutTokens.bottomNavInset }
              : { paddingBottom: padBottom },
            constrain,
            contentContainerStyle,
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          refreshControl={refreshControl}
        >
          {children}
        </ScrollView>
      ) : (
        <View style={[styles.flex, constrain]}>{children}</View>
      )}
    </View>
  );
}

/** Bottom padding a FlatList/own-scroller should use to clear the tab bar. */
export const screenBottomInset = layoutTokens.bottomNavInset;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  flex: {
    flex: 1,
  },
});
